(() => {
  if (customElements.get('quiet-ritual-hero')) return;

  class QuietRitualHero extends HTMLElement {
    constructor() {
      super();
      this.mobileViewport = window.matchMedia('(max-width: 749px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = true;
      this.handoffRaf = 0;
      this.resizeRaf = 0;
      this.stableSizingRaf = 0;
      this.lateSizingTimer = 0;

      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onTransitionScroll = this.onTransitionScroll.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
    }

    connectedCallback() {
      this.videos = [...this.querySelectorAll('.dqr-hero__video')];
      this.desktopVideo = this.querySelector('.dqr-hero__video--desktop');
      this.mobileVideo = this.querySelector('.dqr-hero__video--mobile');
      this.classList.add('is-entered');
      this.classList.toggle('is-reduced-motion', this.isReducedMotion);

      this.videos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.addEventListener('playing', () => this.onVideoPlaying(video));
        video.addEventListener('error', () => this.onVideoError(video));
      });

      this.observer = new IntersectionObserver((entries) => {
        this.visible = this.isThemeEditor || (entries[0]?.isIntersecting ?? false);
        this.classList.toggle('is-paused', !this.isThemeEditor && (!this.visible || document.hidden));
        this.updatePlayback();
      }, { threshold: 0 });
      this.observer.observe(this);

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      window.addEventListener('scroll', this.onTransitionScroll, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onViewportChange, { passive: true });
      this.mobileViewport.addEventListener?.('change', this.onViewportChange);
      this.reduceMotion.addEventListener?.('change', this.onMotionPreferenceChange);

      this.setViewportSizing();
      this.scheduleStableViewportSizing();
      this.setNextSectionColor();
      this.updatePlayback();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('scroll', this.onTransitionScroll);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      this.mobileViewport.removeEventListener?.('change', this.onViewportChange);
      this.reduceMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
      if (this.handoffRaf) cancelAnimationFrame(this.handoffRaf);
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      if (this.stableSizingRaf) cancelAnimationFrame(this.stableSizingRaf);
      if (this.lateSizingTimer) clearTimeout(this.lateSizingTimer);
      this.videos?.forEach((video) => video.pause());
      this.nextSection?.classList.remove('dqr-handoff-next');
      this.sectionWrapper?.style.removeProperty('--dqr-top-offset');
      this.sectionWrapper?.style.removeProperty('--dqr-viewport-height');
      this.sectionWrapper?.style.removeProperty('--dqr-content-height');
    }

    get isMobile() {
      return this.mobileViewport.matches;
    }

    get isReducedMotion() {
      const query = new URLSearchParams(window.location.search);
      return this.reduceMotion.matches || query.get('dqr_motion') === 'reduce';
    }

    get isThemeEditor() {
      return Boolean(
        window.Shopify?.designMode ||
        document.documentElement.classList.contains('shopify-design-mode')
      );
    }

    get isIOSSafari() {
      const userAgent = navigator.userAgent || '';
      const isIOS = /iP(?:ad|hone|od)/.test(userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isSafari = /Safari/i.test(userAgent) && !/(?:CriOS|FxiOS|EdgiOS|OPiOS)/i.test(userAgent);
      return isIOS && isSafari;
    }

    get activeVideo() {
      return this.isMobile ? this.mobileVideo : this.desktopVideo;
    }

    updatePlayback() {
      const active = this.activeVideo;
      const canPlay = Boolean(
        active &&
        this.dataset.liquidEnabled === 'true' &&
        !this.isReducedMotion &&
        (this.isThemeEditor || (this.visible && !document.hidden))
      );

      this.videos?.forEach((video) => {
        if (video !== active || !canPlay) video.pause();
      });

      if (!canPlay) {
        this.dataset.liquidRunning = 'false';
        this.dataset.liquidStatus = this.isReducedMotion ? 'reduced-motion' : 'paused';
        return;
      }

      const playPromise = active.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          this.dataset.liquidRunning = 'false';
          this.dataset.liquidStatus = 'autoplay-blocked';
        });
      }
    }

    onVideoPlaying(video) {
      if (video !== this.activeVideo) {
        video.pause();
        return;
      }
      this.classList.add('is-liquid-ready');
      this.classList.remove('is-liquid-fallback');
      this.dataset.liquidLevel = 'video';
      this.dataset.liquidStatus = 'ready';
      this.dataset.liquidRunning = 'true';
    }

    onVideoError(video) {
      if (video !== this.activeVideo) return;
      this.classList.remove('is-liquid-ready');
      this.classList.add('is-liquid-fallback');
      this.dataset.liquidLevel = 'static';
      this.dataset.liquidStatus = 'video-error';
      this.dataset.liquidRunning = 'false';
    }

    onVisibilityChange() {
      this.classList.toggle('is-paused', !this.isThemeEditor && (document.hidden || !this.visible));
      this.updatePlayback();
    }

    onMotionPreferenceChange() {
      this.classList.toggle('is-reduced-motion', this.isReducedMotion);
      this.updatePlayback();
      this.updateHandoff();
    }

    onViewportChange() {
      if (this.isMobile && window.scrollY > 4) return;
      if (this.resizeRaf) return;
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        this.setViewportSizing();
        this.updatePlayback();
        this.updateHandoff();
      });
    }

    scheduleStableViewportSizing() {
      if (this.stableSizingRaf) cancelAnimationFrame(this.stableSizingRaf);
      if (this.lateSizingTimer) clearTimeout(this.lateSizingTimer);

      this.stableSizingRaf = requestAnimationFrame(() => {
        this.stableSizingRaf = requestAnimationFrame(() => {
          this.stableSizingRaf = 0;
          this.setViewportSizing();
        });
      });

      this.lateSizingTimer = window.setTimeout(() => {
        this.lateSizingTimer = 0;
        this.setViewportSizing();
      }, 280);
    }

    setViewportSizing() {
      const sectionWrapper = this.sectionWrapper || this.closest('.shopify-section');
      if (!sectionWrapper) return;
      this.sectionWrapper = sectionWrapper;
      const documentTop = sectionWrapper.getBoundingClientRect().top + window.scrollY;
      const topOffset = Math.max(0, Math.min(160, Math.round(documentTop)));
      const visualHeight = Math.round(window.visualViewport?.height || window.innerHeight);
      const layoutHeight = Math.max(
        Math.round(window.innerHeight || 0),
        Math.round(document.documentElement?.clientHeight || 0)
      );
      const safariScreenHeight = this.isIOSSafari ? Math.round(window.screen?.height || 0) : 0;
      const viewportHeight = Math.max(visualHeight, layoutHeight, safariScreenHeight);
      sectionWrapper.style.setProperty('--dqr-top-offset', `${topOffset}px`);
      sectionWrapper.style.setProperty('--dqr-viewport-height', `${Math.max(560, viewportHeight - topOffset)}px`);
      sectionWrapper.style.setProperty('--dqr-content-height', `${Math.max(560, visualHeight - topOffset)}px`);
    }

    onTransitionScroll() {
      // Preserve native scrolling; update only the visual transition.
      if (this.handoffRaf) return;
      this.handoffRaf = requestAnimationFrame(() => {
        this.handoffRaf = 0;
        this.updateHandoff();
      });
    }

    updateHandoff() {
      if (!this.nextSection) return;
      if (this.isReducedMotion) {
        this.style.setProperty('--dqr-media-opacity', '1');
        this.style.setProperty('--dqr-transition-opacity', '1');
        this.dataset.handoffProgress = '1.000';
        return;
      }
      const travel = Math.max(140, Math.min(190, window.innerHeight * 0.2));
      const progress = Math.min(1, Math.max(0, window.scrollY / travel));
      this.style.setProperty('--dqr-media-opacity', (1 - progress * 0.08).toFixed(3));
      this.style.setProperty('--dqr-transition-opacity', (0.16 + progress * 0.84).toFixed(3));
      this.dataset.handoffProgress = progress.toFixed(3);
    }

    setNextSectionColor() {
      const sectionWrapper = this.sectionWrapper || this.closest('.shopify-section');
      const nextSection = sectionWrapper?.nextElementSibling;
      if (!nextSection) return;
      this.nextSection = nextSection;
      if (this.dataset.bottomTransition !== 'true') return;
      nextSection.classList.add('dqr-handoff-next');
      const candidates = [nextSection, ...[...nextSection.children].slice(0, 8)].filter(Boolean);
      const resolved = candidates
        .map((node) => getComputedStyle(node).backgroundColor)
        .find((color) => color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent');
      if (resolved) this.style.setProperty('--dqr-next-bg', resolved);
      this.updateHandoff();
    }
  }

  customElements.define('quiet-ritual-hero', QuietRitualHero);
})();
